import { Squares2X2Icon } from "@heroicons/react/24/outline";
import {
    activityCategories,
    ActivityCategory,
    activityCategoryLabels,
    ActivityTarget,
    activityTargetFromView,
    asActivityCategory,
    isAnySelected,
    TaskTypeId,
} from "@sp-crm/core";
import type { AppRouter } from "@sp-crm/server/rpc";
import type { inferProcedureOutput } from "@trpc/server";
import { activityCategoryIcons } from "components/tasks/activity-category-icons";
import { ActivityTargetChips, anyActivityLabel } from "components/tasks/activity-chip";
import { DeleteButton, EditButton } from "components/ui/action-button";
import { AutosavingInput } from "components/ui/autosaving-input";
import { ButtonSelect, ButtonSelectOption } from "components/ui/button-select";
import { PrimaryCompactButton } from "components/ui/primary-button";
import { SecondaryButton, SecondaryCompactButton } from "components/ui/secondary-button";
import { Select } from "components/ui/select";
import { useGetTaskTypesQuery } from "generated/graphql";
import React from "react";
import { stableQueryOptions } from "util/requests";
import {
    activityTargetViews,
    ActivityTypeOption,
    asCadenceUnitInput,
    cadenceUnitOptions,
    describeInterval,
    EditableTouchpoint,
    emptyEditableTouchpoint,
    isWholeCategorySelected,
    parseCadenceCount,
    selectedArchivedTypesInCategory,
    selectedTypeIdsInCategory,
    suggestedTouchpointsForTier,
    toggleAny,
    toggleTypeInCategory,
    toggleWholeCategory,
    typeOptionsForCategory,
} from "./touchpoint-cadence";

type Touchpoint = inferProcedureOutput<
    AppRouter["marketingPlaybook"]["agencyPlaybook"]
>["touchpoints"][number];

const ANY_OPTION_ID = "any";

const sectionLabelClasses = "text-base font-medium text-gray-800";

interface ActivityTypeOptions {
    options: ActivityTypeOption[];
    isResolved: boolean;
}

const useActivityTypeOptions = (): ActivityTypeOptions => {
    const taskTypeQuery = useGetTaskTypesQuery({}, stableQueryOptions());
    const taskTypes = taskTypeQuery.data?.getTaskTypes;
    return {
        options: (taskTypes ?? []).map(taskType => ({
            taskTypeId: taskType.id,
            name: taskType.name,
            category: asActivityCategory(taskType.category),
            isArchived: taskType.isArchived,
        })),
        isResolved: taskTypes !== undefined,
    };
};

const fromTouchpoint = (touchpoint: Touchpoint): EditableTouchpoint => ({
    targets: touchpoint.targets.map(activityTargetFromView),
    intervalUnit: asCadenceUnitInput(touchpoint.intervalUnit),
    intervalCount: touchpoint.intervalCount,
});

interface ChipRowProps {
    label: React.ReactNode;
    options: ButtonSelectOption[];
    selectedIds: string[];
    onToggle: (id: string) => void;
}

interface RowLabelProps {
    icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
    text: string;
}

const RowLabel: React.FC<RowLabelProps> = ({ icon: Icon, text }) => (
    <>
        <Icon aria-hidden="true" className="h-4 w-4 flex-shrink-0 text-gray-500" />
        <span className="text-xs font-medium text-gray-500">{text}</span>
    </>
);

const ChipRow: React.FC<ChipRowProps> = ({ label, options, selectedIds, onToggle }) => (
    <div className="flex flex-wrap items-start gap-x-2">
        <div className="my-2 flex w-16 flex-shrink-0 items-center gap-1 py-1">
            {label}
        </div>
        <div className="min-w-0 flex-1">
            <ButtonSelect
                options={options}
                selectedIds={selectedIds}
                onToggle={onToggle}
            />
        </div>
    </div>
);

interface CategoryRowProps {
    category: ActivityCategory;
    activityTypes: ActivityTypeOption[];
    targets: ActivityTarget[];
    onChange: (targets: ActivityTarget[]) => void;
}

const CategoryChipRow: React.FC<CategoryRowProps> = ({
    category,
    activityTypes,
    targets,
    onChange,
}) => {
    const CategoryIcon = activityCategoryIcons[category];
    const categoryLabel = activityCategoryLabels[category];
    const archivedInUse = selectedArchivedTypesInCategory(
        targets,
        category,
        activityTypes,
    );
    const options: ButtonSelectOption[] = [
        {
            id: ANY_OPTION_ID,
            label: `Any ${categoryLabel}`,
            icon: Squares2X2Icon,
        },
        ...typeOptionsForCategory(activityTypes, category).map(option => ({
            id: option.taskTypeId,
            label: option.name,
        })),
        ...archivedInUse.map(option => ({
            id: option.taskTypeId,
            label: `${option.name} (archived)`,
        })),
    ];
    const selectedIds = isWholeCategorySelected(targets, category)
        ? [ANY_OPTION_ID]
        : selectedTypeIdsInCategory(targets, category, activityTypes);
    const toggle = (id: string) =>
        onChange(
            id === ANY_OPTION_ID
                ? toggleWholeCategory(targets, category, activityTypes)
                : toggleTypeInCategory(
                      targets,
                      category,
                      id as TaskTypeId,
                      activityTypes,
                  ),
        );
    return (
        <ChipRow
            label={<RowLabel icon={CategoryIcon} text={categoryLabel} />}
            options={options}
            selectedIds={selectedIds}
            onToggle={toggle}
        />
    );
};

interface EditCardProps {
    activityTypes: ActivityTypeOption[];
    value: EditableTouchpoint;
    onChange: (value: EditableTouchpoint) => void;
    footer: (state: { canSave: boolean }) => React.ReactNode;
}

const TouchpointEditCard: React.FC<EditCardProps> = ({
    activityTypes,
    value,
    onChange,
    footer,
}) => {
    const canSave = value.targets.length > 0;
    const anyOption: ButtonSelectOption[] = [
        { id: ANY_OPTION_ID, label: anyActivityLabel, icon: Squares2X2Icon },
    ];
    return (
        <div className="space-y-4 rounded-md border border-gray-300 bg-gray-50 p-3 shadow-sm">
            <div className="text-lg font-medium text-gray-900">
                Configure scheduled activity
            </div>
            <div>
                <div className={sectionLabelClasses}>Activity types</div>
                <ChipRow
                    label={<RowLabel icon={Squares2X2Icon} text="Any" />}
                    options={anyOption}
                    selectedIds={isAnySelected(value.targets) ? [ANY_OPTION_ID] : []}
                    onToggle={() =>
                        onChange({ ...value, targets: toggleAny(value.targets) })
                    }
                />
                {activityCategories.map(category => (
                    <CategoryChipRow
                        key={category}
                        category={category}
                        activityTypes={activityTypes}
                        targets={value.targets}
                        onChange={targets => onChange({ ...value, targets })}
                    />
                ))}
            </div>
            <div className="space-y-1">
                <div className={sectionLabelClasses}>Cadence</div>
                <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-600">every</span>
                    <div className="w-14 [&_input]:text-sm">
                        <AutosavingInput
                            type="number"
                            min={1}
                            initial={String(value.intervalCount)}
                            onCommit={input => {
                                const count = parseCadenceCount(input);
                                if (count !== null) {
                                    onChange({ ...value, intervalCount: count });
                                }
                            }}
                        />
                    </div>
                    <div className="w-24">
                        <Select
                            textSize="sm"
                            value={value.intervalUnit}
                            onChange={e =>
                                onChange({
                                    ...value,
                                    intervalUnit: asCadenceUnitInput(e.target.value),
                                })
                            }>
                            {cadenceUnitOptions.map(option => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </Select>
                    </div>
                </div>
            </div>
            <div className="space-y-1">
                <div className={sectionLabelClasses}>Summary</div>
                {canSave ? (
                    <div className="flex flex-wrap items-center gap-1 text-sm text-gray-600">
                        <ActivityTargetChips
                            targets={activityTargetViews(value.targets, activityTypes)}
                        />
                        <span className="font-medium text-gray-800">
                            {describeInterval(value.intervalCount, value.intervalUnit)}
                        </span>
                    </div>
                ) : (
                    <div className="text-sm text-amber-600">
                        Pick at least one activity type for this schedule.
                    </div>
                )}
            </div>
            {footer({ canSave })}
        </div>
    );
};

interface SavedRowProps {
    touchpoint: Touchpoint;
    activityTypes: ActivityTypeOption[];
    disabled: boolean;
    onSave: (id: string, value: EditableTouchpoint) => void;
    onDelete: (id: string) => void;
}

const SavedTouchpointRow: React.FC<SavedRowProps> = ({
    touchpoint,
    activityTypes,
    disabled,
    onSave,
    onDelete,
}) => {
    const [editing, setEditing] = React.useState(false);
    const [draft, setDraft] = React.useState<EditableTouchpoint>(() =>
        fromTouchpoint(touchpoint),
    );

    if (editing) {
        return (
            <TouchpointEditCard
                activityTypes={activityTypes}
                value={draft}
                onChange={setDraft}
                footer={({ canSave }) => (
                    <div className="flex items-center gap-2">
                        <PrimaryCompactButton
                            type="button"
                            disabled={!canSave}
                            onClick={() => {
                                onSave(touchpoint.id, draft);
                                setEditing(false);
                            }}>
                            Save
                        </PrimaryCompactButton>
                        <SecondaryCompactButton
                            type="button"
                            onClick={() => setEditing(false)}>
                            Cancel
                        </SecondaryCompactButton>
                        <div className="ml-auto">
                            <DeleteButton
                                backgroundColor="bg-gray-100"
                                size="sm"
                                onClick={() => onDelete(touchpoint.id)}
                            />
                        </div>
                    </div>
                )}
            />
        );
    }

    return (
        <div className="flex flex-wrap items-center gap-1">
            <ActivityTargetChips targets={touchpoint.targets} />
            <span className="text-sm text-gray-700">
                {describeInterval(
                    touchpoint.intervalCount,
                    asCadenceUnitInput(touchpoint.intervalUnit),
                )}
            </span>
            <EditButton
                backgroundColor="bg-white"
                size="sm"
                disabled={disabled}
                onClick={() => {
                    setDraft(fromTouchpoint(touchpoint));
                    setEditing(true);
                }}
            />
        </div>
    );
};

interface NewRowProps {
    activityTypes: ActivityTypeOption[];
    initial: EditableTouchpoint;
    onCreate: (value: EditableTouchpoint) => void;
    onCancel: () => void;
}

const NewTouchpointRow: React.FC<NewRowProps> = ({
    activityTypes,
    initial,
    onCreate,
    onCancel,
}) => {
    const [draft, setDraft] = React.useState<EditableTouchpoint>(initial);
    return (
        <TouchpointEditCard
            activityTypes={activityTypes}
            value={draft}
            onChange={setDraft}
            footer={({ canSave }) => (
                <div className="flex items-center gap-2">
                    <PrimaryCompactButton
                        type="button"
                        disabled={!canSave}
                        onClick={() => onCreate(draft)}>
                        Save
                    </PrimaryCompactButton>
                    <SecondaryCompactButton type="button" onClick={onCancel}>
                        Cancel
                    </SecondaryCompactButton>
                </div>
            )}
        />
    );
};

interface NewRow {
    key: string;
    initial: EditableTouchpoint;
}

interface Props {
    touchpoints: Touchpoint[];
    newRows: NewRow[];
    tierKey: string;
    disabled: boolean;
    onAddRow: (initial: EditableTouchpoint) => void;
    onSaveTouchpoint: (id: string, value: EditableTouchpoint) => void;
    onDeleteTouchpoint: (id: string) => void;
    onCreateTouchpoint: (newRowKey: string, value: EditableTouchpoint) => void;
    onCancelNewRow: (newRowKey: string) => void;
}

export const TierTouchpointsEditor: React.FC<Props> = ({
    touchpoints,
    newRows,
    tierKey,
    disabled,
    onAddRow,
    onSaveTouchpoint,
    onDeleteTouchpoint,
    onCreateTouchpoint,
    onCancelNewRow,
}) => {
    const { options: activityTypes, isResolved } = useActivityTypeOptions();
    const isEmpty = touchpoints.length === 0 && newRows.length === 0;
    const suggestions = suggestedTouchpointsForTier(tierKey);
    const editingDisabled = disabled || !isResolved;
    return (
        <div className="space-y-2 pl-1">
            <div className="text-sm font-medium text-gray-500">Scheduled activities</div>
            {isEmpty ? (
                <div className="text-sm text-gray-400">
                    Marketers will be prompted to reach out to their assigned referral
                    sources in this tier based on the activities you schedule. Pick the
                    activity types that count toward each schedule.
                </div>
            ) : (
                <div className="space-y-2">
                    {touchpoints.map(touchpoint => (
                        <SavedTouchpointRow
                            key={touchpoint.id}
                            touchpoint={touchpoint}
                            activityTypes={activityTypes}
                            disabled={editingDisabled}
                            onSave={onSaveTouchpoint}
                            onDelete={onDeleteTouchpoint}
                        />
                    ))}
                    {newRows.map(row => (
                        <NewTouchpointRow
                            key={row.key}
                            activityTypes={activityTypes}
                            initial={row.initial}
                            onCreate={value => onCreateTouchpoint(row.key, value)}
                            onCancel={() => onCancelNewRow(row.key)}
                        />
                    ))}
                </div>
            )}
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                <SecondaryButton
                    className="whitespace-nowrap"
                    disabled={editingDisabled}
                    onClick={() => onAddRow(emptyEditableTouchpoint())}>
                    Add activity
                </SecondaryButton>
                {isEmpty && suggestions.length > 0 ? (
                    <SecondaryButton
                        className="whitespace-nowrap"
                        disabled={editingDisabled}
                        onClick={() => suggestions.forEach(onAddRow)}>
                        Use recommended schedule
                    </SecondaryButton>
                ) : null}
            </div>
        </div>
    );
};
